import { Box, Drawer, Typography, IconButton } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import UploadFile from 'components/Common/UploadFile';
import { useEffect, useState } from 'react';
import { useCreateOrUpdateDataSet, useGetDataSet } from 'apis/business/setting/knowledgeBase/hook';
import DataSetFormSkeleton from './DataSetFormSkeleton';
import { DataSetSchema } from 'utils/schema/validator/DataSetValidation';
import { zodResolver } from '@hookform/resolvers/zod';

export default function DataSetForm() {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const [searchParams] = useSearchParams();

  const datasetType = searchParams.get('datasetType');
  const isBlankDatasetType = datasetType === 'blank';

  const [selectedFiles, setSelectedFiles] = useState<null | any[]>(null);
  const { data: dataset, isLoading: isDataSetLoading } = useGetDataSet();
  const { mutateAsync: handleDataSet, isPending } = useCreateOrUpdateDataSet();

  const handleChange = async (files: File[]) => {
    const data = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      data.push(file);
    }
    setSelectedFiles(data);
  };

  const { handleSubmit, control, watch, reset } = useForm<any>({
    defaultValues: { datasource_type: 'document' },
    values: dataset ?? { name: '', datasource_type: 'document' },
    resolver: zodResolver(DataSetSchema)
  });

  const active = watch('datasource_type');
  const nameValue = watch('name');

  useEffect(() => {
    if (!dataset) {
      reset({
        name: nameValue,
        datasource_type: active
      });
    }
  }, [reset, active, nameValue, dataset]);

  const onSubmit = (data: any) => {
    const formData = new FormData();
    if (isBlankDatasetType) {
      formData.append('dataset[datasource_type]', 'blank');
      formData.append('dataset[name]', data?.name);
    } else {
      prepareFormData(formData, data, 'dataset');
      if (selectedFiles) {
        selectedFiles.forEach((file) => {
          formData.append('documents[]', file);
        });
      }
    }
    handleDataSet(formData);
  };

  const selectedDatasourceType = watch('datasource_type');

  const allowedFiles = allowedFilesMap[selectedDatasourceType] || [];

  if (isDataSetLoading) {
    return <DataSetFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 800 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>Configuration</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          <Grid container spacing={3} alignItems='center'>
            <Grid size={12}>
              <Input name='name' label='Name' control={control} fullWidth required sx={{ marginTop: 0.5 }} />
            </Grid>
            {!datasetId && !isBlankDatasetType && (
              <>
                <Grid overflow='auto' size={12}>
                  {watch('datasource_type') === 'document' && (
                    <UploadFile
                      selectedFiles={selectedFiles}
                      handleChange={handleChange}
                      allowedFileTypes={allowedFiles}
                    />
                  )}
                  {watch('datasource_type') === 'website' && (
                    <Input name='configuration.url' label='Url' control={control} fullWidth />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: 2,
            flexShrink: 0
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{ border: '1px solid #F3F3F3' }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            {datasetId ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export const allowedFilesMap: {
  [key: string]: string[];
} = {
  document: ['docx', 'xlsx', 'pdf', 'txt', 'md', 'json', 'html'],
  qa: ['xlsx', 'csv']
};

const prepareFormData = (formData: FormData, data: Record<string, any>, parentKey: string = '') => {
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        prepareFormData(formData, value, formKey);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          prepareFormData(formData, { [`${key}[${index}]`]: item }, parentKey);
        });
      } else {
        formData.append(formKey, value);
      }
    }
  }
};
